import { reachedRouteMilestone } from "@healthos/shared";
/**
 * Which milestones are worth asking the server to claim right now.
 *
 * At most one per subject, and it is always the latest state: the finish if the
 * route is done, otherwise the furthest waypoint reached. Short routes make
 * this matter. Walk a 5km route in one session and you cross every waypoint and
 * finish it in a single sync; offering all of them would open five celebrations
 * back to back and turn the moment into wallpaper.
 *
 * The server owns once-only, so calling this repeatedly is safe. The caller
 * only has to avoid re-asking about a ref it has already sent this session.
 */
export function pendingMilestoneClaims(enrollment, progress) {
    if (!enrollment)
        return [];
    const own = latestClaim(enrollment, enrollment.progress / Math.max(1, enrollment.target), null);
    // Only a group the viewer belongs to. The route map shows every walking
    // group in the company, since visibility decides who may join and not who
    // may look, so an unfiltered pass here celebrates other people's groups at
    // members who were never in them and offers them a share button for it.
    const groupClaims = (progress?.groups ?? []).flatMap((group) => {
        if (!group.viewerRole)
            return [];
        const claim = latestClaim(enrollment, group.progressFraction, group.id);
        return claim ? [claim] : [];
    });
    return own ? [own, ...groupClaims] : groupClaims;
}
/** The finish if the route is done, else the furthest waypoint, else nothing. */
function latestClaim(enrollment, progressFraction, groupId) {
    if (progressFraction >= 1) {
        return {
            kind: "completion",
            enrollmentId: enrollment.enrollmentId,
            milestoneIndex: Math.max(0, enrollment.routeMilestones.length - 1),
            groupId,
        };
    }
    const reached = reachedRouteMilestone(enrollment.routeMilestones, progressFraction);
    if (!reached)
        return null;
    return {
        kind: "waypoint",
        enrollmentId: enrollment.enrollmentId,
        milestoneIndex: reached.index,
        groupId,
    };
}
/** Stable identity for a claim, so a client can avoid re-asking about one. */
export function milestoneClaimKey(claim) {
    return `${claim.kind}:${claim.enrollmentId}:${claim.milestoneIndex}:${claim.groupId ?? "self"}`;
}
export function routeMilestoneCopy(celebration, options = {}) {
    const isGroup = celebration.subject === "group";
    const isFinish = celebration.kind === "completion";
    const percent = Math.round(celebration.progressFraction * 100);
    const remainingMeters = isFinish || !celebration.distanceMeters
        ? null
        : Math.max(0, Math.round(celebration.distanceMeters * (1 - celebration.progressFraction)));
    const subject = isGroup
        ? (celebration.groupName ?? "Your group")
        : options.viewerFirstName?.trim() || "You";
    const pooled = `pooled across ${celebration.memberCount ?? 0} ${celebration.memberCount === 1 ? "member" : "members"}`;
    return {
        subject,
        eyebrow: isFinish
            ? isGroup
                ? "GROUP ROUTE COMPLETE"
                : "ROUTE COMPLETE"
            : isGroup
                ? "GROUP WAYPOINT REACHED"
                : "WAYPOINT REACHED",
        // The finish names the route, since the route is the achievement. A
        // waypoint names the place, since the route is still underway.
        title: isFinish ? celebration.routeName : celebration.milestoneTitle,
        description: isFinish
            ? `${celebration.routeName}, end to end. Every ordinary day of movement added up to the whole thing.`
            : celebration.milestoneDescription,
        standing: isFinish
            ? `All ${celebration.milestoneCount} ${celebration.milestoneCount === 1 ? "waypoint" : "waypoints"} on ${celebration.routeName}`
            : `Waypoint ${celebration.milestoneOrdinal} of ${celebration.milestoneCount} on ${celebration.routeName}`,
        progress: isFinish
            ? isGroup
                ? `The whole route, ${pooled}`
                : "The whole route"
            : isGroup
                ? `${percent}% of the route, ${pooled}`
                : `${percent}% of the route`,
        remainingMeters,
    };
}
/** The feed sentence for a shared milestone, from the stored event payload. */
export function routeMilestoneFeedLine(payload, firstName) {
    const who = payload.subject === "group" && payload.groupName
        ? `${firstName} and ${payload.groupName}`
        : firstName;
    if (payload.kind === "completion") {
        return `${who} completed ${payload.routeName}, end to end.`;
    }
    return `${who} reached ${payload.milestoneTitle} on ${payload.routeName}.`;
}
//# sourceMappingURL=route-milestone.js.map